"use client";

import Cal from "@calcom/embed-react";
import { isCompanyEmailDomain } from "@repo/common";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, MailCheck } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { SALES_BOOKING_CAL_LINK } from "@/app/(marketing)/_components/nav-links";
import { setSignupSourceCookie } from "@/app/actions/signup-source";
import { AUDIENCE_HERO, AuthHeroPanel } from "@/components/auth/audience-hero";
import { TeracWordmark } from "@/components/icons/TeracWordmark";
import { signIn, signOut } from "@/lib/auth-client";
import { MAGIC_LINK_EXPIRES_IN_LABEL } from "@/lib/magic-link";
import { setSignupIntent } from "@/lib/signup-intent";
import { setTermsAcceptanceIntent } from "@/lib/terms-intent";

import { getResearcherBookingRoute } from "./actions";
import {
  type BookingAttribution,
  bookingCalConfig,
  bookingDraftSchema,
  isCompanyWebsite,
  type Lead,
  RESEARCH_TYPES,
  researchTypeAnswerOf,
  researchTypeOf,
} from "./booking-lead";
import {
  qualifyResearcherBookingLead,
  requestResearcherBookingFeasibility,
} from "./lead-qualification-actions";

/**
 * 280ms on a strong ease-in-out: the columns are morphing on screen rather
 * than entering, and it stays under the 300ms an interface can afford. Named
 * properties only, never `all`, and dropped entirely for reduced motion, where
 * a resizing column is the part that causes trouble.
 */
const MORPH =
  "transition-[grid-template-columns,max-width] duration-[280ms] ease-[cubic-bezier(0.77,0,0.175,1)] motion-reduce:transition-none";

const EMPTY_LEAD: Lead = {
  firstName: "",
  lastName: "",
  companyWebsite: "",
  researchType: "",
  otherResearchType: "",
  referrer: "",
};

const fieldClass =
  "border-border text-foreground focus:border-marketing-blue dark:focus:border-marketing-blue-light w-full rounded-2xl border bg-transparent px-4 py-[13px] text-[14.5px] outline-none placeholder:text-neutral-500 dark:placeholder:text-neutral-400";

const labelClass =
  "text-foreground mb-1.5 block text-[13px] font-medium tracking-[-0.01em]";

/**
 * Signed-out entry point for researchers. Terac does not take research buyers
 * self-serve, so the calendar is the primary path and the sign-in form sits
 * behind the link below the form for people who already have an account.
 */
export function ResearcherBooking({
  signInHref,
  signedInAs,
  verifiedEmail,
  verificationFailed,
  bookingDetails,
  attribution,
}: {
  signInHref: string;
  /** Set when the page rendered this against a live session with no org. */
  signedInAs?: string;
  verifiedEmail?: string;
  verificationFailed: boolean;
  bookingDetails?: string;
  /** Where this visit came from, as the page read it off the URL and cookie. */
  attribution?: BookingAttribution;
}) {
  const router = useRouter();
  const [initialDraft] = useState(() => {
    if (!bookingDetails) {
      return;
    }
    try {
      const bytes = Uint8Array.from(
        atob(bookingDetails.replaceAll("-", "+").replaceAll("_", "/")),
        (char) => char.charCodeAt(0),
      );
      const result = bookingDraftSchema.safeParse(
        JSON.parse(new TextDecoder().decode(bytes)),
      );
      if (
        result.success &&
        result.data.expiresAt > Date.now() &&
        (!verifiedEmail ||
          result.data.email.toLowerCase() === verifiedEmail.toLowerCase())
      ) {
        return result.data;
      }
    } catch {
      // Callback details are untrusted; they never establish email ownership.
    }
  });
  // The draft's copy wins: it was read before the magic-link round trip, which
  // may finish in a browser that never saw the campaign.
  const leadAttribution = initialDraft?.attribution ?? attribution;
  const [lead, setLead] = useState<Lead>(initialDraft?.lead ?? EMPTY_LEAD);
  const [websiteTouched, setWebsiteTouched] = useState(false);
  const websiteInput = useRef<HTMLInputElement>(null);
  const websiteError =
    websiteTouched &&
    lead.companyWebsite.trim() !== "" &&
    !isCompanyWebsite(lead.companyWebsite);
  const [booking, setBooking] = useState(false);
  // Screening kept the calendar back: sales will follow up instead.
  const [reviewing, setReviewing] = useState(false);
  // A verified callback resumes straight into screening, so the form shows as
  // busy until the answer arrives rather than flashing editable.
  const resumeVerified = Boolean(
    verifiedEmail && initialDraft && !verificationFailed,
  );
  const [checkingSession, setCheckingSession] = useState(resumeVerified);
  const [email, setEmail] = useState(
    verifiedEmail ?? signedInAs ?? initialDraft?.email ?? "",
  );
  const [sentEmail, setSentEmail] = useState("");
  const [sending, setSending] = useState(false);
  const bookingEmail = verifiedEmail ?? email.trim().toLowerCase();
  const personalEmail =
    bookingEmail.includes("@") &&
    bookingEmail.split("@")[1]!.includes(".") &&
    !isCompanyEmailDomain(bookingEmail);

  const sendLink = async () => {
    if (sending) {
      return;
    }
    setSending(true);
    try {
      const draft = bookingDraftSchema.parse({
        email: bookingEmail,
        lead,
        attribution: leadAttribution,
        expiresAt: Date.now() + 60 * 60 * 1000,
      });
      const route = await getResearcherBookingRoute(draft.email);
      if (route === "rate-limited") {
        toast.error("Too many attempts. Please wait a minute and try again.");
        return;
      }
      if (route === "sign-in") {
        router.replace(
          `${signInHref}&email=${encodeURIComponent(draft.email)}`,
        );
        return;
      }
      if (personalEmail) {
        return;
      }
      // Base64url survives the auth callback's extra URL decoding, including
      // research answers containing &, %, + or non-ASCII characters.
      const bytes = new TextEncoder().encode(JSON.stringify(draft));
      const encodedDetails = btoa(String.fromCharCode(...bytes))
        .replaceAll("+", "-")
        .replaceAll("/", "_")
        .replace(/=+$/, "");
      const callbackURL = `/researchers/login?booking=${encodedDetails}`;
      await setSignupSourceCookie("researcher_login_page");
      setSignupIntent("researcher");
      setTermsAcceptanceIntent(true);
      const { error } = await signIn.magicLink({
        email: draft.email,
        name: `${draft.lead.firstName} ${draft.lead.lastName}`,
        callbackURL,
        newUserCallbackURL: callbackURL,
        errorCallbackURL: callbackURL,
      });
      if (error) {
        throw new Error(error.message || "Please try again.");
      }
      setSentEmail(draft.email);
    } catch (error) {
      toast.error("Couldn't send verification link", {
        description:
          error instanceof Error ? error.message : "Please try again.",
      });
    } finally {
      setSending(false);
    }
  };

  const update = (key: keyof Lead) => (value: string) => {
    setLead((current) => ({ ...current, [key]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (checkingSession || sending) {
      return;
    }
    if (!isCompanyWebsite(lead.companyWebsite)) {
      setWebsiteTouched(true);
      websiteInput.current?.focus();
      return;
    }
    if (!verifiedEmail) {
      await sendLink();
      return;
    }
    await qualify();
  };

  // Screening runs on the server against the session, so a verified email
  // alone never opens the calendar.
  const qualify = async () => {
    setCheckingSession(true);
    try {
      const result = await qualifyResearcherBookingLead({
        firstName: lead.firstName,
        lastName: lead.lastName,
        website: lead.companyWebsite,
        researchType: researchTypeAnswerOf(lead),
        referrer: lead.referrer,
      });
      if (result === "unverified") {
        window.location.replace("/researchers/login");
        return;
      }
      if (result === "review") {
        setReviewing(true);
        return;
      }
      setBooking(true);
    } catch (error) {
      toast.error("Couldn't continue to the calendar", {
        description:
          error instanceof Error ? error.message : "Please try again.",
      });
    } finally {
      setCheckingSession(false);
    }
  };

  const resumed = useRef(false);
  useEffect(() => {
    if (resumeVerified && !resumed.current) {
      resumed.current = true;
      void qualify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, on arrival from the verification link
  }, []);

  // Reduced motion keeps the crossfade, which is what explains the swap, and
  // loses the travel, which is what makes people ill.
  const lift = useReducedMotion() ? 0 : 8;

  const hero = AUDIENCE_HERO.researcher;

  const handleLogout = async () => {
    try {
      await signOut();
      // Full-document nav, not router.push: a soft nav keeps the SPA's
      // in-memory state, so Back would restore this authenticated page.
      window.location.replace("/login");
    } catch (error) {
      toast.error("Couldn't log out", {
        description:
          error instanceof Error ? error.message : "Please try again.",
      });
    }
  };

  return (
    // The sign-in shell, matched rather than re-invented: this route swaps
    // between booking and signing in, and the two must not look like different
    // products.
    <div
      className={`bg-marketing-cream text-foreground dark:bg-background grid min-h-screen grid-cols-1 ${MORPH} ${
        booking
          ? "lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)]"
          : "lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]"
      }`}
    >
      <AuthHeroPanel
        audience="researcher"
        // The marks seat themselves once, against the panel's size at that
        // moment, so a resize strands whichever is in flight half off the new
        // edge. Off while the panel is narrowed. Remounting the panel instead
        // was worse: the seats are chosen before the copy has settled, and a
        // card landed on top of the headline.
        marks={!booking}
        headline={hero.headline}
        subtitle={hero.subtitle}
        panelClass={hero.panelClass}
        // The shipped ramp is keyed to the viewport on the assumption that this
        // panel is half of it. On the calendar step it is 1/2.6, so the
        // headline is sized against the panel instead: 3.2vw holds "Run
        // research with" on one line at that share, and the marks reseat
        // themselves once it stops overflowing.
        headlineClassName={
          booking ? "text-[clamp(26px,3.2vw,58px)]" : undefined
        }
      />

      {/* Centred on the form, which fits, and top-aligned on the calendar,
          which does not: centring something taller than its column puts half
          the overflow above the top edge, under the cross-link. Keyed off the
          step rather than `justify-content: safe center`, which Chrome drops as
          invalid and leaves computing to `normal`. Only from `lg`, because
          below it the hero is hidden and a short phone viewport is the overflow
          case again.

          Symmetric py so the centre is the real centre, and deep enough that
          the top-aligned step still clears the cross-link. */}
      <div
        className={`relative flex flex-col px-6 py-[76px] sm:px-10 lg:py-20 ${
          booking ? "lg:justify-start" : "lg:justify-center"
        }`}
      >
        <Link
          href="/"
          aria-label="Terac home"
          className="absolute top-6 left-6 sm:top-8 sm:left-10 lg:hidden"
        >
          <TeracWordmark className="text-foreground h-9 w-auto" />
        </Link>

        {/* Hidden on the calendar step: the person is mid-booking, and an exit
            to the other audience is not what that step is for. */}
        {!booking && (
          <Link
            href="/login"
            className="absolute top-6 right-6 inline-flex items-center gap-2 text-[13px] sm:top-8 sm:right-10"
          >
            <span className="hidden text-[#606060] sm:inline dark:text-neutral-400">
              Looking for paid interviews?
            </span>
            <span className="text-foreground underline underline-offset-[3px]">
              Log in to take part →
            </span>
          </Link>
        )}

        {/* mode="wait" so the two steps never overlap inside a column that is
            resizing underneath them. Opacity carries the swap and survives
            reduced motion; the small lift is what gets dropped. */}
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={booking ? "calendar" : reviewing ? "review" : "form"}
            initial={{ opacity: 0, y: lift }}
            animate={{ opacity: 1, y: 0 }}
            // Leaving is faster than arriving: the system is responding, not
            // asking the reader to take anything in.
            exit={{
              opacity: 0,
              y: -lift,
              transition: { duration: 0.12, ease: [0.23, 1, 0.32, 1] },
            }}
            transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
            // mx-auto, not m-auto like the sign-in forms: those are short
            // enough to centre, and both of these steps are taller than the
            // column. Centring the overflow pushed the heading up into the band
            // the padding reserves for the cross-link.
            className={`mx-auto flex w-full flex-col ${MORPH} ${
              booking ? "max-w-none" : "max-w-[440px]"
            }`}
          >
            {booking && verifiedEmail ? (
              <BookingCalendar
                lead={lead}
                verifiedEmail={verifiedEmail}
                attribution={leadAttribution}
                onBack={() => {
                  setBooking(false);
                }}
              />
            ) : reviewing && verifiedEmail ? (
              <LeadReview
                firstName={lead.firstName}
                email={verifiedEmail}
                onBack={() => {
                  setReviewing(false);
                }}
              />
            ) : sentEmail ? (
              <div role="status">
                <MailCheck className="text-marketing-blue mb-4 size-8" />
                <h2 className="text-foreground text-[27px] font-medium tracking-[-0.02em]">
                  Check your email
                </h2>
                <p className="mt-3 text-[15px] text-[#606060] dark:text-neutral-400">
                  We sent a verification link to{" "}
                  <span className="text-foreground font-medium break-all">
                    {sentEmail}
                  </span>
                  . Open it to pick a time. Your booking will use this email.
                  The link expires in {MAGIC_LINK_EXPIRES_IN_LABEL}.
                </p>
                <button
                  type="button"
                  disabled={sending}
                  onClick={() => void sendLink()}
                  className="text-foreground mt-5 cursor-pointer underline underline-offset-4 disabled:opacity-60"
                >
                  {sending ? "Sending..." : "Resend verification link"}
                </button>
                <button
                  type="button"
                  disabled={sending}
                  onClick={() => {
                    setSentEmail("");
                  }}
                  className="mt-3 block cursor-pointer text-[13px] text-[#606060] underline underline-offset-4 disabled:opacity-60 dark:text-neutral-400"
                >
                  Back to your details
                </button>
              </div>
            ) : (
              <>
                {/* No eyebrow: the two sign-ins on this shell dropped theirs,
                    and it landed on the cross-link's line and collided with it.
                    Left-aligned rather than centred like those headings,
                    because a six-field form reads from the left and a centred
                    heading over left-aligned labels looks like two decisions. */}
                <h2 className="text-foreground m-0 text-[27px] leading-[1.15] font-medium tracking-[-0.02em] min-[1024px]:text-[30px] min-[1440px]:text-[34px]">
                  Talk to our team
                </h2>
                <p className="mt-2 text-[14px] text-[#606060] lg:text-[15px] dark:text-neutral-400">
                  {verifiedEmail
                    ? "Your work email is verified. Pick a time after confirming your details."
                    : "Tell us a little about you. We'll email you a link to verify your work email, then you can pick a time."}
                </p>
                {verificationFailed && !verifiedEmail && (
                  <p
                    role="alert"
                    className="mt-4 text-[13px] text-red-600 dark:text-red-400"
                  >
                    This verification link is invalid or has expired. Submit
                    your details to request a new one.
                  </p>
                )}

                <form
                  onSubmit={handleSubmit}
                  className="mt-[22px] flex flex-col gap-4"
                >
                  <fieldset
                    disabled={sending || checkingSession}
                    className="flex flex-col gap-4"
                  >
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <label htmlFor="firstName" className={labelClass}>
                          First name
                        </label>
                        <input
                          id="firstName"
                          required
                          value={lead.firstName}
                          onChange={(e) => {
                            update("firstName")(e.target.value);
                          }}
                          placeholder="Ada"
                          autoComplete="given-name"
                          className={fieldClass}
                        />
                      </div>
                      <div className="flex-1">
                        <label htmlFor="lastName" className={labelClass}>
                          Last name
                        </label>
                        <input
                          id="lastName"
                          required
                          value={lead.lastName}
                          onChange={(e) => {
                            update("lastName")(e.target.value);
                          }}
                          placeholder="Lovelace"
                          autoComplete="family-name"
                          className={fieldClass}
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="companyWebsite" className={labelClass}>
                        Company website
                      </label>
                      <input
                        ref={websiteInput}
                        id="companyWebsite"
                        required
                        value={lead.companyWebsite}
                        onChange={(e) => {
                          update("companyWebsite")(e.target.value);
                        }}
                        onBlur={() => {
                          setWebsiteTouched(true);
                        }}
                        placeholder="acmeresearch.com"
                        inputMode="url"
                        autoComplete="url"
                        autoCapitalize="none"
                        spellCheck={false}
                        aria-invalid={websiteError || undefined}
                        aria-describedby={
                          websiteError ? "companyWebsite-error" : undefined
                        }
                        className={fieldClass}
                      />
                      {websiteError && (
                        <p
                          id="companyWebsite-error"
                          className="mt-1.5 text-[13px] text-red-600 dark:text-red-400"
                        >
                          Enter your company&apos;s own website, like
                          acmeresearch.com.
                        </p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="companyEmail" className={labelClass}>
                        Work email
                      </label>
                      <input
                        id="companyEmail"
                        type="email"
                        required
                        value={bookingEmail}
                        readOnly={Boolean(verifiedEmail)}
                        onChange={(event) => {
                          setEmail(event.target.value);
                        }}
                        placeholder="ada@acmeresearch.com"
                        autoComplete="email"
                        aria-invalid={personalEmail || undefined}
                        aria-describedby={
                          personalEmail
                            ? "companyEmail-error"
                            : verifiedEmail
                              ? "companyEmail-verified"
                              : undefined
                        }
                        className={fieldClass}
                      />
                      {verifiedEmail && (
                        <p
                          id="companyEmail-verified"
                          className="mt-1.5 text-[13px] text-[#606060] dark:text-neutral-400"
                        >
                          Verified. Your booking will use this email.
                        </p>
                      )}
                      {personalEmail && (
                        <p
                          id="companyEmail-error"
                          className="mt-1.5 text-[13px] text-red-600 dark:text-red-400"
                        >
                          New bookings require a work email. Already a
                          researcher? Continue to sign in.
                        </p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="researchType" className={labelClass}>
                        Type of research
                      </label>
                      <select
                        id="researchType"
                        required
                        value={lead.researchType}
                        onChange={(e) => {
                          update("researchType")(e.target.value);
                        }}
                        className={fieldClass}
                      >
                        <option value="" disabled>
                          Select one
                        </option>
                        {RESEARCH_TYPES.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {lead.researchType === "other" && (
                      <div>
                        <label
                          htmlFor="otherResearchType"
                          className={labelClass}
                        >
                          Which kind?
                        </label>
                        <input
                          id="otherResearchType"
                          required
                          value={lead.otherResearchType}
                          onChange={(e) => {
                            update("otherResearchType")(e.target.value);
                          }}
                          placeholder="Ethnographic fieldwork"
                          className={fieldClass}
                        />
                      </div>
                    )}

                    <div>
                      <label htmlFor="referrer" className={labelClass}>
                        How did you hear about Terac?
                      </label>
                      <input
                        id="referrer"
                        required
                        value={lead.referrer}
                        onChange={(e) => {
                          update("referrer")(e.target.value);
                        }}
                        placeholder="A colleague, LinkedIn, a search"
                        className={fieldClass}
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={sending || checkingSession}
                      className="bg-marketing-blue hover:bg-marketing-blue-dark border-marketing-blue dark:border-marketing-blue-light inline-flex w-full cursor-pointer items-center justify-center rounded-full border px-4 py-[13px] text-[14.5px] font-medium tracking-[-0.01em] text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {sending
                        ? "Sending..."
                        : checkingSession
                          ? "Continuing..."
                          : "Continue"}
                    </button>
                  </fieldset>
                </form>
                {!verifiedEmail && (
                  <p className="mt-3 text-center text-[11.5px] text-neutral-500 dark:text-neutral-400">
                    By continuing, you agree to our{" "}
                    <Link
                      href="/legal/terms-of-service"
                      target="_blank"
                      className="underline"
                    >
                      Terms
                    </Link>{" "}
                    and{" "}
                    <Link
                      href="/legal/privacy-policy"
                      target="_blank"
                      className="underline"
                    >
                      Privacy Policy
                    </Link>
                    .
                  </p>
                )}
              </>
            )}

            {!booking && (
              <p className="mt-4 text-center text-[13px] text-[#606060] dark:text-neutral-400">
                {signedInAs ? (
                  <>
                    Logged in as {signedInAs}.{" "}
                    <button
                      type="button"
                      onClick={() => {
                        void handleLogout();
                      }}
                      className="text-foreground border-foreground cursor-pointer border-b pb-px font-medium"
                    >
                      Log out →
                    </button>
                  </>
                ) : (
                  <>
                    Already have an account?{" "}
                    <Link
                      href={signInHref}
                      className="text-foreground border-foreground border-b pb-px font-medium"
                    >
                      Log in
                    </Link>
                  </>
                )}
              </p>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

/** Second step, rendered in the same panel the form just occupied. */
function BookingCalendar({
  lead,
  verifiedEmail,
  attribution,
  onBack,
}: {
  lead: Lead;
  verifiedEmail: string;
  attribution?: BookingAttribution;
  onBack: () => void;
}) {
  const researchType = researchTypeOf(lead);

  return (
    <>
      <button
        type="button"
        onClick={onBack}
        className="mb-5 inline-flex w-fit cursor-pointer items-center gap-1.5 text-[13.5px] text-[#606060] dark:text-neutral-400"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back
      </button>

      <h2 className="text-foreground m-0 text-[30px] leading-[1.15] font-medium tracking-[-0.02em]">
        Pick a time, {lead.firstName}
      </h2>
      <p className="mt-3 text-[15px] text-[#606060] dark:text-neutral-400">
        Thirty minutes with our team to scope what you need and get your
        workspace set up.
      </p>

      <Cal
        // Remounts when the type changes, because the whole calendar belongs
        // to whoever owns that link.
        key={researchType?.calLink}
        calLink={researchType?.calLink ?? SALES_BOOKING_CAL_LINK}
        // No fixed height: the embed reports its own, so the compact calendar
        // and the taller confirm step each get what they need instead of
        // scrolling inside the iframe.
        className="mt-5 w-full"
        config={{
          ...bookingCalConfig({ lead, email: verifiedEmail, attribution }),
          layout: "month_view",
        }}
      />
    </>
  );
}

const HOURS_PER_DAY = 24;

/**
 * Second step when screening withholds the calendar. Worded the same whatever
 * the reason: sales reviews the lead, and a brief they can send now gets them
 * a price without waiting for a call.
 */
function LeadReview({
  firstName,
  email,
  onBack,
}: {
  firstName: string;
  email: string;
  onBack: () => void;
}) {
  const [who, setWho] = useState("");
  const [what, setWhat] = useState("");
  const [count, setCount] = useState("");
  const [days, setDays] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (sending) {
      return;
    }
    setSending(true);
    try {
      const people = Number.parseInt(count, 10);
      const within = Number.parseInt(days, 10);
      const result = await requestResearcherBookingFeasibility({
        panelDescription: who,
        taskDescription: what,
        submissionCount: people > 0 ? people : null,
        timelineHours: within > 0 ? within * HOURS_PER_DAY : null,
      });
      if (result === "unverified") {
        window.location.replace("/researchers/login");
        return;
      }
      if (result === "rate-limited") {
        toast.error("Too many requests. Please try again in an hour.");
        return;
      }
      setSent(true);
    } catch (error) {
      toast.error("Couldn't send your request", {
        description:
          error instanceof Error ? error.message : "Please try again.",
      });
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div role="status">
        <MailCheck className="text-marketing-blue mb-4 size-8" />
        <h2 className="text-foreground text-[27px] font-medium tracking-[-0.02em]">
          Request sent
        </h2>
        <p className="mt-3 text-[15px] text-[#606060] dark:text-neutral-400">
          We&apos;ll reply to{" "}
          <span className="text-foreground font-medium break-all">{email}</span>{" "}
          with feasibility and a price per participant.
        </p>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={onBack}
        className="mb-5 inline-flex w-fit cursor-pointer items-center gap-1.5 text-[13.5px] text-[#606060] dark:text-neutral-400"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back
      </button>

      <h2 className="text-foreground m-0 text-[27px] leading-[1.15] font-medium tracking-[-0.02em] min-[1024px]:text-[30px]">
        Thanks, {firstName}. We&apos;ll be in touch.
      </h2>
      <p className="mt-2 text-[14px] text-[#606060] lg:text-[15px] dark:text-neutral-400">
        Our team reviews each request before booking a call and will reply to{" "}
        <span className="text-foreground font-medium break-all">{email}</span>.
        Already know who you need? Send us the brief and we&apos;ll come back
        with feasibility and a price per participant, no call needed.
      </p>

      <form onSubmit={submit} className="mt-[22px] flex flex-col gap-4">
        <fieldset disabled={sending} className="flex flex-col gap-4">
          <div>
            <label htmlFor="feasibilityWho" className={labelClass}>
              Who are you looking for?
            </label>
            <textarea
              id="feasibilityWho"
              required
              minLength={5}
              rows={3}
              value={who}
              onChange={(e) => {
                setWho(e.target.value);
              }}
              placeholder="Heads of Finance at US companies with 200+ employees"
              className={`${fieldClass} resize-y`}
            />
          </div>

          <div>
            <label htmlFor="feasibilityWhat" className={labelClass}>
              What will they do?
            </label>
            <textarea
              id="feasibilityWhat"
              required
              minLength={5}
              rows={2}
              value={what}
              onChange={(e) => {
                setWhat(e.target.value);
              }}
              placeholder="A 30-minute video interview about choosing a payroll vendor"
              className={`${fieldClass} resize-y`}
            />
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <label htmlFor="feasibilityCount" className={labelClass}>
                How many people?
              </label>
              <input
                id="feasibilityCount"
                inputMode="numeric"
                value={count}
                onChange={(e) => {
                  setCount(e.target.value.replace(/\D/g, ""));
                }}
                placeholder="Optional"
                className={fieldClass}
              />
            </div>
            <div className="flex-1">
              <label htmlFor="feasibilityDays" className={labelClass}>
                Needed within (days)
              </label>
              <input
                id="feasibilityDays"
                inputMode="numeric"
                value={days}
                onChange={(e) => {
                  setDays(e.target.value.replace(/\D/g, ""));
                }}
                placeholder="Optional"
                className={fieldClass}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={sending}
            className="bg-marketing-blue hover:bg-marketing-blue-dark border-marketing-blue dark:border-marketing-blue-light inline-flex w-full cursor-pointer items-center justify-center rounded-full border px-4 py-[13px] text-[14.5px] font-medium tracking-[-0.01em] text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60"
          >
            {sending ? "Sending..." : "Send request"}
          </button>
        </fieldset>
      </form>
    </>
  );
}
